import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allMangas, categories } from '@/mocks/manga';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const manga = allMangas.find((m) => m.id === id);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您查找的漫画不存在或已被移除</p>
            <Link to="/" className="text-sm text-primary-500 hover:underline cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMangas = allMangas
    .filter((m) => m.id !== manga.id && m.categories.some((c) => manga.categories.includes(c)))
    .slice(0, 4);

  const chapterList = Array.from({ length: Math.min(manga.chapters, 12) }, (_, i) => ({
    number: manga.chapters - i,
    title: `第${manga.chapters - i}话`,
    date: '2026-07-06',
  }));

  const categorySlugs = manga.categories.map((cat) => {
    const found = categories.find((c) => c.name === cat);
    return found ? found.slug : 'all';
  });

  const primaryCategorySlug = categorySlugs[0] || 'all';
  const primaryCategoryName = manga.categories[0] || '分类';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://waiwaimanhua.com/' },
      { '@type': 'ListItem', position: 2, name: primaryCategoryName, item: `https://waiwaimanhua.com/category/${primaryCategorySlug}` },
      { '@type': 'ListItem', position: 3, name: manga.title, item: `https://waiwaimanhua.com/comic/${manga.id}` },
    ],
  };

  const comicJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: manga.title,
    author: { '@type': 'Person', name: manga.author },
    description: manga.description,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: manga.rating,
      bestRating: '10',
      reviewCount: Math.floor(manga.views / 10000),
    },
    image: manga.coverImage,
    inLanguage: 'zh-CN',
    publisher: { '@type': 'Organization', name: '歪歪漫画网' },
    genre: manga.categories,
    url: `https://waiwaimanhua.com/comic/${manga.id}`,
    dateModified: manga.updatedAt,
    numberOfPages: manga.chapters,
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 韩国漫画在线阅读`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    image: manga.coverImage,
    datePublished: '2024-01-15',
    dateModified: manga.updatedAt,
    publisher: { '@type': 'Organization', name: '歪歪漫画网' },
    url: `https://waiwaimanhua.com/comic/${manga.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(comicJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main className="pt-20 bg-background-50 min-h-screen">
        <section className="px-4 md:px-6 py-8">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li>
                <Link to={`/category/${primaryCategorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                  {primaryCategoryName}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li>
                <span className="text-foreground-700">{manga.title}</span>
              </li>
            </ol>
          </nav>

          <article className="flex flex-col lg:flex-row gap-6 lg:gap-10 mb-10">
            <div className="lg:w-80 flex-shrink-0">
              <div className="rounded-xl overflow-hidden shadow-sm aspect-[3/4] max-w-[300px] mx-auto lg:mx-0">
                <img
                  src={manga.coverImage}
                  alt={manga.title}
                  title={`${manga.title} 高清封面 - 歪歪漫画网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {manga.title}
              </h1>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="flex items-center gap-1 text-sm">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="font-semibold text-foreground-800">{manga.rating}</span>
                  <span className="text-foreground-400">/10</span>
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>
                  {manga.views.toLocaleString()}次阅读
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  manga.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-foreground-100 text-foreground-600'
                }`}>
                  {manga.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {manga.categories.map((cat) => (
                  <Link
                    key={cat}
                    to={`/category/${categories.find((c) => c.name === cat)?.slug || 'all'}`}
                    className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {cat}
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-400 mb-1">作者</p>
                  <p className="text-sm font-medium text-foreground-800">{manga.author}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-400 mb-1">总章节</p>
                  <p className="text-sm font-medium text-foreground-800">{manga.chapters}话</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-400 mb-1">更新时间</p>
                  <p className="text-sm font-medium text-foreground-800">{manga.updatedAt}</p>
                </div>
              </div>

              <div className="mb-5">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
              </div>

              <div className="flex items-center gap-2">
                <button className="flex-1 flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-play-fill"></i>
                  开始阅读
                </button>
                <button className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-heart-3-line text-lg"></i>
                </button>
                <button className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer">
                  <i className="ri-share-line text-lg"></i>
                </button>
              </div>
            </div>
          </article>

          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-foreground-900">章节列表</h2>
              <span className="text-sm text-foreground-500">共{manga.chapters}话</span>
            </div>
            <div className="bg-background-100 rounded-xl p-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1">
                {chapterList.map((chapter) => (
                  <button
                    key={chapter.number}
                    className="text-center py-2.5 rounded-lg bg-background-50 hover:bg-accent-50 hover:text-accent-600 text-sm text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {chapter.title}
                  </button>
                ))}
              </div>
              {manga.chapters > 12 && (
                <div className="text-center py-3">
                  <button className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer">
                    查看全部{manga.chapters}话 <i className="ri-arrow-down-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {relatedMangas.length > 0 && (
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {relatedMangas.map((m) => (
                  <Link
                    key={m.id}
                    to={`/comic/${m.id}`}
                    className="group block rounded-xl overflow-hidden bg-background-100 transition-transform duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={m.coverImage}
                        alt={m.title}
                        title={`${m.title} - 歪歪漫画网相关推荐`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          m.status === '连载中'
                            ? 'bg-accent-500/90 text-background-50'
                            : 'bg-foreground-800/80 text-background-50'
                        }`}>
                          {m.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                        {m.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{m.author} · {m.rating}分</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}